import type { GuideCommand } from "@soluto-private/expert-workspace-timeline";

import { useFeatures, type WorkspaceFeatures } from "@expert/features";
import { Stack } from "@mantine/core";
import { type JSX, useEffect, useMemo } from "react";
import { withErrorBoundary } from "react-error-boundary";

import { usePartnerConfig, usePartnerFeatureConfigured } from "@/partner/usePartnerConfig";
import { useActiveTask, useAgentStore, useCallbackState, useSession } from "@/sdk";
import { useLogger } from "@/sdk/logging/LoggerProvider";
import { DeprecatedSessionSummary, SessionSummary, useSessionSummaryStore } from "@/session-summary";
import { createFallbackRender } from "@/shared-utils/fallbackRender";
import { PlatformTimeline } from "@/timeline";

import styles from "./workspace.module.css";

interface WorkspaceBodyProps {
    guideToolboxCommand: GuideCommand;
}

export function WorkspaceBody({ guideToolboxCommand }: WorkspaceBodyProps): JSX.Element {
    const logger = useLogger({ module: "WorkspaceBody" });

    const PlatformTimelineHandled = useMemo(
        () => withErrorBoundary(PlatformTimeline, { fallbackRender: createFallbackRender(logger) }),
        [logger],
    );
    const SessionSummaryHandled = useMemo(
        () => withErrorBoundary(SessionSummary, { fallbackRender: createFallbackRender(logger) }),
        [logger],
    );
    const DeprecatedSessionSummaryHandled = useMemo(
        () =>
            withErrorBoundary(DeprecatedSessionSummary, {
                fallbackRender: createFallbackRender(logger),
            }),
        [logger],
    );

    // Hooks
    const session = useSession();
    const callbackState = useCallbackState();
    const activeTask = useActiveTask();
    const { activity: currentActivity } = useAgentStore((state) => state);
    const { simplifiedSummary, callControlsEnabled } = usePartnerFeatureConfigured();
    const {
        features: { isUpdatedEndSessionFlowEnabled },
    } = useFeatures<WorkspaceFeatures>();
    const { setIsUpdatedEndSessionFlowEnabled } = useSessionSummaryStore();
    const { wrappingAgentActivity } = usePartnerConfig();

    const isCustomerSession = session.kind === "with-customer";

    // State
    const hasPendingCallback = callbackState?.callbackType === "CallbackNow";
    // Callback failures can leave us without an active task, so we also treat a customer session wrappingState
    // as a valid wrap signal for rendering summary.
    const hasActiveCustomerSessionWrappingState = session.kind === "with-customer" && !!session.wrappingState;

    // Summary is shown only when we have an explicit wrap signal:
    // 1) wrapping agent activity, or
    // 2) active task marked wrapping, or
    // 3) session-level wrapping state for customer sessions.
    const isWrappingState =
        currentActivity === wrappingAgentActivity ||
        activeTask?.status === "wrapping" ||
        hasActiveCustomerSessionWrappingState;

    const displaySummary = isCustomerSession && isWrappingState && !hasPendingCallback;

    useEffect(() => {
        // store this feature flag in a zustand store so it can be accessed in the session lifecycle methods
        setIsUpdatedEndSessionFlowEnabled(isUpdatedEndSessionFlowEnabled);
    }, [isUpdatedEndSessionFlowEnabled, setIsUpdatedEndSessionFlowEnabled]);

    return (
        <>
            <Stack className={styles.workspaceBody} gap="sm" pt={!callControlsEnabled ? "0" : undefined}>
                <PlatformTimelineHandled guide={guideToolboxCommand} />
            </Stack>

            {displaySummary &&
                (isUpdatedEndSessionFlowEnabled && !simplifiedSummary ? (
                    <SessionSummaryHandled />
                ) : (
                    <DeprecatedSessionSummaryHandled />
                ))}
        </>
    );
}
